import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ClipboardList, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const LOCATIES = [["warehouse", "Eigen warehouse"], ["leverancier", "Leverancier"], ["channeldock", "ChannelDock"]];

export default function VoorraadAudits() {
  const [items, setItems] = useState([]);
  const [opties, setOpties] = useState({ merken: [], categorieen: [] });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    naam: "", start_datum: "", eind_datum: "", merken: [], categorieen: [],
    locaties: ["warehouse", "leverancier", "channeldock"], notitie: "",
  });

  const load = async () => {
    const [{ data }, { data: o }] = await Promise.all([
      api.get("/voorraad-audits"), api.get("/voorraad-audits/redenen"),
    ]);
    setItems(data); setOpties(o);
  };
  useEffect(() => { load().catch(() => toast.error("Laden mislukt")); }, []);

  const toggle = (veld, waarde) => setF(p => ({
    ...p, [veld]: p[veld].includes(waarde) ? p[veld].filter(x => x !== waarde) : [...p[veld], waarde],
  }));

  const maak = async () => {
    if (!f.naam.trim() || !f.start_datum || !f.eind_datum) return toast.error("Naam en beide datums zijn verplicht");
    try {
      const { data } = await api.post("/voorraad-audits", f);
      toast.success("Audit aangemaakt");
      setOpen(false); setItems(p => [data, ...p]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Aanmaken mislukt"); }
  };

  const verwijder = async (id) => {
    if (!window.confirm("Deze audit verwijderen?")) return;
    await api.delete(`/voorraad-audits/${id}`);
    setItems(p => p.filter(x => x.id !== id));
  };

  return (
    <div className="space-y-6" data-testid="voorraadaudits">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl">Voorraadaudit</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Sluitende voorraadadministratie per periode (bijv. voor Certisys): beginvoorraad + inkoop − verkoop
            ± correcties − verlies = eindvoorraad.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="audit-nieuw-btn"><Plus className="h-4 w-4 mr-2" />Nieuwe audit</Button>
          </DialogTrigger>
          <DialogContent data-testid="audit-nieuw-dialog">
            <DialogHeader><DialogTitle>Nieuwe voorraadaudit</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Naam</Label>
                <Input value={f.naam} onChange={(e) => setF({ ...f, naam: e.target.value })}
                  placeholder="Certisys audit 2026" data-testid="audit-naam" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Peildatum beginvoorraad</Label>
                  <Input type="date" value={f.start_datum} onChange={(e) => setF({ ...f, start_datum: e.target.value })}
                    data-testid="audit-start" /></div>
                <div><Label>Auditdatum</Label>
                  <Input type="date" value={f.eind_datum} onChange={(e) => setF({ ...f, eind_datum: e.target.value })}
                    data-testid="audit-eind" /></div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Merken (leeg = alle)</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {opties.merken.map(m => (
                    <Button key={m} type="button" size="sm" variant={f.merken.includes(m) ? "default" : "outline"}
                      onClick={() => toggle("merken", m)} data-testid={`audit-merk-${m}`}>{m}</Button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Categorieën (leeg = alle)</Label>
                <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto">
                  {opties.categorieen.map(c => (
                    <Button key={c} type="button" size="sm" variant={f.categorieen.includes(c) ? "default" : "outline"}
                      onClick={() => toggle("categorieen", c)}>{c}</Button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Locaties meenemen</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {LOCATIES.map(([k, label]) => (
                    <Button key={k} type="button" size="sm" variant={f.locaties.includes(k) ? "default" : "outline"}
                      onClick={() => toggle("locaties", k)} data-testid={`audit-locatie-${k}`}>{label}</Button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
              <Button onClick={maak} data-testid="audit-opslaan">Aanmaken</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-border rounded-xl divide-y divide-border">
        {items.map((a) => (
          <div key={a.id} className="p-4 flex items-center justify-between gap-3" data-testid={`audit-rij-${a.id}`}>
            <Link to={`/voorraadaudit/${a.id}`} className="min-w-0 flex-1 group">
              <div className="font-medium flex items-center gap-2 group-hover:text-primary">
                <ClipboardList className="h-4 w-4 text-primary" />{a.naam}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {a.start_datum} t/m {a.eind_datum} · {(a.merken || []).join(", ") || "alle merken"} ·
                {" "}{(a.locaties || []).join(", ")}
              </div>
            </Link>
            <Badge variant={a.status === "afgerond" ? "outline" : "default"}>{a.status}</Badge>
            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => verwijder(a.id)}
              data-testid={`audit-verwijder-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 text-muted-foreground italic">Nog geen audits — maak er één aan.</p>}
      </div>
    </div>
  );
}
